/**
 * Recursively processes the liferayFields object, converting specific string values
 * to their designated equivalents (e.g., 'null' -> '', 'true' -> true, 'false' -> false, etc.).
 * This function specifically handles cases where certain string values are meant
 * to be interpreted as other data types or values, such as turning 'null' into an empty string.
 *
 * @param obj - The object or array to be processed.
 * @returns The transformed object or array with values converted as needed.
 *
 * @example
 * const input = {
 *   key1: 'null',
 *   key2: 'true',
 *   key3: '',
 *   key4: ['null', 'true', 'false', '[]', []]
 * };
 *
 * const output = liferayParser(input);
 * // Output: { key1: '', key2: true, key3: '', key4: ['', true, false, '', []] }
 */

export function liferayParser<T>(obj: T): T {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => {
      if (item === 'null' || item === '') return ''
      if (item === '[]') return ''
      if (item === 'true') return true
      if (item === 'false') return false

      return liferayParser(item)
    }) as unknown as T
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    switch (value) {
      case 'null':
      case '':
        acc[key as keyof T] = '' as unknown as T[keyof T]
        break
      case '[]':
        acc[key as keyof T] = [] as unknown as T[keyof T]
        break
      case 'true':
        acc[key as keyof T] = true as unknown as T[keyof T]
        break
      case 'false':
        acc[key as keyof T] = false as unknown as T[keyof T]
        break
      default:
        acc[key as keyof T] = (
          typeof value === 'object' ? liferayParser(value) : value
        ) as T[keyof T]
        break
    }

    return acc
  }, {} as T)
}
